import { tr } from "../i18n";
import { PageHero, CTABand } from "./Shared";
import { Reveal, Stagger, StaggerItem, Kicker, SectionHead } from "../components/motion";
import { ArrowRight } from "lucide-react";
const CHAPTERS = [{
  n: "01",
  title: "Independence is the foundation",
  copy: "ESAIA does not develop, implement or sell AI systems. Our only role is to assess. This separation is what gives an ESAIA finding its weight: the assessor has nothing to gain from the outcome."
}, {
  n: "02",
  title: "Evidence over intention",
  copy: "Responsible AI principles are easy to publish and hard to prove. ESAIA assessments are built on documented evidence, observed practice and verifiable governance structures, not on statements of intent."
}, {
  n: "03",
  title: "Assurance must be continuous",
  copy: "AI is a fast-moving environment. Models are updated, agents are added, data shifts and new use cases appear every quarter. What is true about an AI environment today may no longer hold six months from now."
}, {
  n: "04",
  title: "Clarity about what we are not",
  copy: "ESAIA is not a government authority and does not issue regulatory approvals. Our certification is a voluntary, independent assurance assessment. Legal compliance remains the responsibility of each organization and its advisors."
}];
const APPROACH = ["Assessment", "Evidence", "Findings", "Improvement", "Assurance", "Reassessment"];
export default function About() {
  return <div data-testid="about-page">
      <PageHero kicker={tr("About ESAIA")} title={tr("Building trust around artificial intelligence.")} copy={tr("ESAIA is an independent Belgian organization focused on AI assurance, ethical assessment and responsible AI practices. Our mission is to help organizations understand, manage and continuously improve the way artificial intelligence is developed and used.")} />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Reveal>
            <div className="h-full border border-esaia-line bg-white p-8 sm:p-10" data-testid="mission-card">
              <Kicker>{tr("Our Mission")}</Kicker>
              <p className="mt-5 font-serif text-2xl font-light leading-snug text-esaia-ink sm:text-3xl">{tr("To strengthen trust in artificial intelligence through independent assessment, structured standards and continuous assurance.")}{" "}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-esaia-line bg-white p-8 sm:p-10" data-testid="vision-card">
              <Kicker>{tr("Our Vision")}</Kicker>
              <p className="mt-5 font-serif text-2xl font-light leading-snug text-esaia-ink sm:text-3xl">{tr("A world where organizations can confidently demonstrate that their use of AI is responsible, transparent and continuously governed.")}{" "}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <SectionHead kicker={tr("Our principles")} title={tr("What ESAIA stands for.")} copy={tr("Four chapters define how ESAIA works and why organizations can rely on its assessments.")} />
          <Stagger className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
            {CHAPTERS.map(c => <StaggerItem key={c.n}>
                <div className="h-full border border-esaia-line bg-esaia-canvas p-8" data-testid={`chapter-${c.n}`}>
                  <span className="font-serif text-4xl font-light text-esaia-blue/40">{c.n}</span>
                  <h3 className="mt-5 text-lg font-semibold text-esaia-ink">{tr(c.title)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(c.copy)}</p>
                </div>
              </StaggerItem>)}
          </Stagger>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead kicker={tr("Independence")} title={tr("Separate from what we assess.")} copy={tr("ESAIA's assessments are performed independently of the development and implementation of the AI systems under review. We do not build the systems we assess, we do not resell vendor technology, and our remuneration is never tied to a positive outcome. This independence allows boards, auditors, customers and partners to rely on ESAIA findings as an external, objective perspective.")} />
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="border border-esaia-line bg-white p-8">
                <Kicker>{tr("Our Approach")}</Kicker>
                <div className="mt-8 flex flex-col gap-0">
                  {APPROACH.map((step, i) => <div key={step} className="flex items-center gap-5 border-b border-esaia-line py-4 last:border-0">
                      <span className="font-mono text-xs tracking-widest text-esaia-subtle">0{i + 1}</span>
                      <span className="text-base font-semibold text-esaia-ink">{tr(step)}</span>
                      {i < APPROACH.length - 1 && <ArrowRight size={14} className="ml-auto text-esaia-blue" aria-hidden="true" />}
                      {i === APPROACH.length - 1 && <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-blue">{tr("and again")}{" "}</span>}
                    </div>)}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand title={tr("Talk to ESAIA about your AI environment.")} copy={tr("Whether you are deploying your first AI system or governing AI across an international group, we can help determine the appropriate assessment scope.")} primary={{
      to: "/contact",
      label: tr("Contact ESAIA")
    }} secondary={{
      to: "/eri",
      label: tr("Take the Free ERI")
    }} />
    </div>;
}
